import { FC, ReactNode } from "react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Building2, Lock, LucideIcon, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dashboard } from "@/models/dashboard/Dashboard";
import {
  DashboardIdentity,
  DashboardIdentityKind,
  parentTenantDisplayName,
} from "../lib/dashboardIdentity";
import { IDENTITY_STYLE } from "../lib/dashboardIdentityStyle";

interface DashboardIdentityBannerProps {
  identity: DashboardIdentity;
  isEditMode: boolean;
  /**
   * The organisation dashboard a personal one is named like (LAR-280): the banner says it is not
   * the one being changed and offers the way there.
   */
  organisationNameClash?: Dashboard | null;
  onOpenDashboard?: (id: string) => void;
}

interface BannerContent {
  /** The organisation banner is coloured like a published one, drafts included. */
  style: DashboardIdentityKind;
  icon: LucideIcon;
  lead: string;
  rest: string;
  action?: ReactNode;
}

/**
 * Who sees what is being changed (LAR-661): grey for a personal dashboard, the brand colour for
 * the organisation's, both only in edit mode. A dashboard inherited from the parent organisation
 * can never be edited here, so its banner stands in for the edit controls at all times.
 */
const DashboardIdentityBanner: FC<DashboardIdentityBannerProps> = ({
  identity,
  isEditMode,
  organisationNameClash,
  onOpenDashboard,
}) => {
  const content = ((): BannerContent | null => {
    if (identity.kind === "parent-readonly") {
      return {
        style: "parent-readonly",
        // A lock rather than the pill's share icon: this banner explains why editing is off.
        icon: Lock,
        lead: `Dette dashboard kommer fra ${parentTenantDisplayName(identity.parentTenantName)}`,
        rest: "og kan ikke redigeres her. Klon det for at lave din egen version.",
      };
    }
    if (!isEditMode) return null;
    if (identity.kind === "personal") {
      return {
        style: "personal",
        icon: User,
        lead: "Du redigerer dit personlige dashboard.",
        rest: organisationNameClash
          ? "Ændringer er kun synlige for dig. Organisationens dashboard med samme navn ændres ikke."
          : "Ændringer er kun synlige for dig.",
        action: organisationNameClash && onOpenDashboard && (
          <Button
            variant="link"
            size="sm"
            className="ml-auto h-auto p-0 font-semibold"
            onClick={() => onOpenDashboard(organisationNameClash.id)}
          >
            Gå til organisationens dashboard
          </Button>
        ),
      };
    }
    return {
      style: "organisation-published",
      icon: Building2,
      lead: "Du redigerer et organisationsdashboard.",
      rest: "Gem, og publicér bagefter, så kollegerne ser ændringerne.",
    };
  })();

  const Icon = content?.icon;

  // The live region stays mounted while the banner comes and goes, because one that appears
  // together with its text is not reliably announced: pressing "Rediger" should be heard.
  return (
    <div role="status" id="dashboard-identity-banner">
      {content && Icon && (
        <div
          className={cn(
            "flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b px-4 py-2.5 text-sm",
            IDENTITY_STYLE[content.style]
          )}
        >
          {/* Icon and text wrap as one unit, so a narrow screen never strands the icon on its
              own line; only the action drops below. */}
          <div className="flex min-w-0 flex-[1_1_20rem] items-start gap-2.5">
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              <strong className="font-semibold">{content.lead}</strong>{" "}
              {content.rest}
            </span>
          </div>
          {content.action}
        </div>
      )}
    </div>
  );
};

export default DashboardIdentityBanner;
